import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, from, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { CuttingService, IBasket, IResponseBasket } from '../cutting.service';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Baskets';
const importConcurrency = 5;

export interface IBasketExportData {
	name: string;
	description?: string;
	capacity: number;
}

const BasketSchema: IFileDataSchema<IBasketExportData> = {
	name: sheetName,
	properties: {
		name: { type: 'string', required: true, fileKey: 'Name', width: 30 },
		description: { type: 'string', required: false, fileKey: 'Description', width: 50 },
		capacity: { type: 'number', required: true, fileKey: 'Capacity', width: 20 },
	},
};

interface BasketImportContext {
	baskets: IResponseBasket[];
}

interface BasketImportCandidate {
	row: IBasketExportData;
	location?: string;
}

@Injectable({
	providedIn: 'root',
})
export class BasketTransferService implements ITransferService<IBasket> {
	private translationPrefix = 'masterdata.cutting.basketImportExport';

	constructor(
		private cuttingService: CuttingService,
		private excelFileTransfer: ExcelFileTransfer<IBasketExportData>,
		private translator: TranslateService,
	) {}
	hasTemplate = (): boolean => true;
	getTemplate = (): void => this.downloadTemplate();

	public exportData(baskets: IBasket[]): Observable<IBasket[]> {
		const duplicateNames = findDuplicateNames(baskets);
		if (duplicateNames.length > 0) {
			return throwError(() => new Error(this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-names.content`, { names: duplicateNames.join(', ') })));
		}

		const data: IBasketExportData[] = baskets.map((basket) => ({
			name: basket.name,
			description: basket.description || '',
			capacity: basket.capacity,
		}));

		return this.excelFileTransfer.exportData(sheetName, data, BasketSchema).pipe(
			map(() => baskets),
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	// A blank starting point for import: the same headers exportData produces, with one filled-in
	// example row instead of real data, so a user knows what each column expects. Every basket
	// field is editable via the create/edit dialog, so unlike setups there's nothing to omit here.
	public downloadTemplate(): void {
		const exampleRow: IBasketExportData = {
			name: 'Example Basket',
			description: 'Example description',
			capacity: 100,
		};

		this.excelFileTransfer.exportData(`${sheetName}Template`, [exampleRow], BasketSchema).subscribe();
	}

	// Duplicate names (file or roster) and per-row validation problems (blank name) all get
	// discovered and reported together in one pass, instead of a user fixing one category,
	// re-importing, and only then discovering the next. Validating a row only needs the row
	// itself, not unambiguous name matching, so it's safe to run even when names collide - only
	// the actual create/update is withheld then, since it's the one step that depends on matching
	// a row to exactly one existing basket by name.
	public importData(file: File | File[]): Observable<ImportResponse> {
		const ctx: BasketImportContext = { baskets: [] };
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((parsedRows) => {
				// Nothing parsed and a real parse failure is already collected - stop here instead
				// of still hitting the roster, whose own failure would otherwise mask the actual
				// workbook parse error the user needs to see.
				if (parsedRows.length === 0 && ImportErrors.length > 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = findDuplicateNames(parsedRows.map((imported) => imported.data));

				return this.cuttingService.getBaskets().pipe(
					mergeMap((baskets) => {
						const rosterDuplicates = findDuplicateNames(baskets.items);

						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						const namesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0;

						ctx.baskets = baskets.items;

						return from(parsedRows).pipe(
							filterMap(this.validateRow(ImportErrors)),
							toArray(),
							mergeMap((validRows) => (namesAreAmbiguous ? of([]) : from(validRows).pipe(mergeMap(this.createOrUpdateBasket(ctx, ImportErrors), importConcurrency), toArray()))),
							map(
								(results) =>
									({
										successfulImports: results.map(
											(r) =>
												({
													Id: r.entity.name,
													status: r.status,
												}) as SuccessFullImport,
										),
										errors: ImportErrors,
									}) as ImportResponse,
							),
						);
					}),
				);
			}),
		);
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, {
				names: names.join(', '),
			}),
		} as FailedParse);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<IBasketExportData>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, BasketSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	private validateRow(ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<IBasketExportData>): BasketImportCandidate | undefined => {
			const { data: row, location } = imported;
			const errors: Record<string, ValidationErrors> = {};

			row.name = (row.name ?? '').trim();

			if (!row.name) {
				errors['name'] = {
					required: this.translator.instant(`${this.translationPrefix}.errors.missing-name`),
				};
			}

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: row.name || '',
					location,
					status: ImportStatus.ValidationError,
					errors,
				} as FailedValidationImport);
				return undefined;
			}

			return { row, location };
		};
	}

	private createOrUpdateBasket(ctx: BasketImportContext, ImportErrors: ImportErrorTypes[]) {
		return (candidate: BasketImportCandidate) => {
			const { row, location } = candidate;
			const basket: IBasket = {
				self: null,
				name: row.name,
				description: row.description || '',
				capacity: row.capacity,
				updatedDate: null,
				updatedBy: null,
			};

			const existingBasket = ctx.baskets.find((b) => normalizeName(b.name) === normalizeName(basket.name));

			if (existingBasket) {
				basket.self = existingBasket.self;
				return this.cuttingService.updateBasket(basket).pipe(
					map(() => ({ entity: basket, status: ImportStatus.SuccessUpdate })),
					catchError((error) => {
						this.pushSaveFailedError(ImportErrors, basket, location, 'update-failed', error);
						return of();
					}),
				);
			}

			return this.cuttingService.createBasket(basket).pipe(
				map(() => ({ entity: basket, status: ImportStatus.SuccessCreate })),
				catchError((error) => {
					this.pushSaveFailedError(ImportErrors, basket, location, 'create-failed', error);
					return of();
				}),
			);
		};
	}

	private saveFailedErrorKeySuffix(error: unknown): string {
		switch ((error as HttpErrorResponse)?.status) {
			case 400:
				return '-bad-request';
			case 404:
				return '-not-found';
			default:
				return '';
		}
	}

	private pushSaveFailedError(ImportErrors: ImportErrorTypes[], basket: IBasket, location: string | undefined, errorKey: 'update-failed' | 'create-failed', error: unknown) {
		ImportErrors.push({
			Id: basket.name,
			location,
			status: ImportStatus.Failed,
			message: this.translator.instant(`${this.translationPrefix}.errors.${errorKey}${this.saveFailedErrorKeySuffix(error)}`, { name: basket.name }),
		});
	}
}

/** Names that appear 2+ times once trimmed/lowercased. Blank names are skipped - a missing name is reported separately as a missing-required-field error, not a collision. Returns the original (non-normalized) spelling of every row involved in a collision, for a message a user can act on. */
function findDuplicateNames(items: { name: string }[]): string[] {
	const firstSeen = new Map<string, string>();
	const duplicates = new Set<string>();

	for (const item of items) {
		const normalized = normalizeName(item.name);
		if (!normalized) {
			continue;
		}

		const original = firstSeen.get(normalized);
		if (original !== undefined) {
			duplicates.add(original);
			duplicates.add(item.name);
		} else {
			firstSeen.set(normalized, item.name);
		}
	}

	return Array.from(duplicates);
}

function normalizeName(name: unknown): string {
	return name === null || name === undefined ? '' : String(name).trim().toLowerCase();
}
